<script setup>
import { computed } from 'vue';
import { Head, router, usePage } from '@inertiajs/vue3';
import {
    PhArrowLeft, PhUser, PhEnvelope, PhPhone, PhMapPin,
    PhCalendar, PhToggleLeft, PhToggleRight, PhFileText,
    PhCurrencyDollar, PhGraduationCap,
} from '@phosphor-icons/vue';
import Layout from '@pages/Shared/layouts/AdminLayout.vue';
import { flashToast } from '@/toast.js';
import userMale   from '@/../images/user-male.png';
import userFemale from '@/../images/user-female.png';

const props = defineProps({
    applicant: Object,
});

const page = usePage();
const can  = computed(() => page.props.can || {});

const statusColor = (status) => ({
    'draft':       'bg-gray-100 text-gray-600',
    'submitted':   'bg-primary-50 text-primary-700',
    'invited':     'bg-indigo-50 text-indigo-700',
    'interviewed': 'bg-purple-50 text-purple-700',
    'admitted':    'bg-green-50 text-green-700',
    'rejected':    'bg-red-50 text-red-600',
    'enrolled':    'bg-emerald-50 text-emerald-700',
}[status] ?? 'bg-gray-100 text-gray-600');

const toggleActive = () => {
    router.patch(`/admin/applicants/${props.applicant.id}/toggle-active`, {}, {
        preserveScroll: true,
        onSuccess: (page) => flashToast(page.props.flash),
    });
};
</script>

<template>
    <Layout>
        <Head :title="`${applicant.first_name} ${applicant.last_name}`" />

        <!-- Back + Header -->
        <div class="flex items-center gap-3 mb-6">
            <button @click="router.get('/admin/applicants')"
                class="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition">
                <PhArrowLeft :size="20" />
            </button>
            <div>
                <h2 class="text-xl font-bold text-primary">Applicant Profile</h2>
                <p class="text-sm text-gray-500">{{ applicant.id }}</p>
            </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

            <!-- Left: Profile card -->
            <div class="lg:col-span-1 space-y-4">
                <div class="bg-white rounded-xl border border-gray-200 p-6 text-center shadow-sm">
                    <img
                        class="w-24 h-24 rounded-full object-cover ring-4 ring-gray-100 mx-auto"
                        :src="applicant.avatar
                            ? `/storage/${applicant.avatar}`
                            : (applicant.gender === 'male' ? userMale : userFemale)"
                        :alt="applicant.first_name"
                    />
                    <h3 class="mt-4 text-lg font-bold text-gray-900">
                        {{ applicant.first_name }} {{ applicant.other_name }} {{ applicant.last_name }}
                    </h3>
                    <p class="text-sm text-gray-500">{{ applicant.id }}</p>

                    <div class="flex items-center justify-center gap-2 mt-3">
                        <span
                            class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
                            :class="applicant.is_active
                                ? 'bg-green-50 text-green-700'
                                : 'bg-orange-50 text-orange-600'"
                        >
                            <span class="w-1.5 h-1.5 rounded-full"
                                :class="applicant.is_active ? 'bg-green-500' : 'bg-orange-400'">
                            </span>
                            {{ applicant.is_active ? 'Active' : 'Inactive' }}
                        </span>
                        <button @click="toggleActive"
                            class="text-gray-400 hover:text-primary transition"
                            :title="applicant.is_active ? 'Deactivate account' : 'Activate account'">
                            <PhToggleRight v-if="applicant.is_active" :size="22" class="text-green-600" />
                            <PhToggleLeft  v-else                      :size="22" />
                        </button>
                    </div>
                </div>

                <!-- Contact info -->
                <div class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-3">
                    <h4 class="text-sm font-semibold text-gray-700 uppercase tracking-wide">Contact</h4>
                    <div class="flex items-center gap-3 text-sm text-gray-600">
                        <PhEnvelope :size="16" class="text-gray-400 shrink-0" />
                        {{ applicant.email }}
                    </div>
                    <div class="flex items-center gap-3 text-sm text-gray-600">
                        <PhPhone :size="16" class="text-gray-400 shrink-0" />
                        {{ applicant.phone ?? '—' }}
                    </div>
                    <div class="flex items-start gap-3 text-sm text-gray-600">
                        <PhMapPin :size="16" class="text-gray-400 shrink-0 mt-0.5" />
                        {{ applicant.address ?? '—' }}
                    </div>
                </div>

                <!-- Guardian -->
                <div class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-3">
                    <h4 class="text-sm font-semibold text-gray-700 uppercase tracking-wide">Guardian</h4>
                    <div class="flex items-center gap-3 text-sm text-gray-600">
                        <PhUser :size="16" class="text-gray-400 shrink-0" />
                        {{ applicant.guardian_name ?? '—' }}
                        <span v-if="applicant.guardian_relationship" class="text-gray-400">
                            ({{ applicant.guardian_relationship }})
                        </span>
                    </div>
                    <div class="flex items-center gap-3 text-sm text-gray-600">
                        <PhPhone :size="16" class="text-gray-400 shrink-0" />
                        {{ applicant.guardian_phone ?? '—' }}
                    </div>
                </div>
            </div>

            <!-- Right: Applications + Payments -->
            <div class="lg:col-span-2 space-y-6">

                <!-- Applications -->
                <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div class="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                        <PhFileText :size="18" class="text-primary" />
                        <h4 class="font-semibold text-gray-800">Applications</h4>
                    </div>
                    <div v-if="applicant.applications?.length" class="divide-y divide-gray-100">
                        <div
                            v-for="app in applicant.applications"
                            :key="app.id"
                            class="px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition cursor-pointer"
                            @click="router.get(`/admin/applications/${app.id}`)"
                        >
                            <div>
                                <p class="text-sm font-medium text-gray-800">
                                    {{ app.academic_session?.name }}
                                    — {{ app.class_room?.name }}
                                </p>
                                <p class="text-xs text-gray-400 mt-0.5">
                                    Submitted: {{ app.submitted_at
                                        ? new Date(app.submitted_at).toLocaleDateString()
                                        : 'Not submitted' }}
                                </p>
                            </div>
                            <span
                                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize"
                                :class="statusColor(app.status)"
                            >
                                {{ app.status }}
                            </span>
                        </div>
                    </div>
                    <div v-else class="px-5 py-10 text-center text-sm text-gray-400">
                        No applications yet.
                    </div>
                </div>

                <!-- Payments -->
                <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div class="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                        <PhCurrencyDollar :size="18" class="text-primary" />
                        <h4 class="font-semibold text-gray-800">Payments</h4>
                    </div>
                    <div v-if="applicant.payments?.length" class="divide-y divide-gray-100">
                        <div
                            v-for="payment in applicant.payments"
                            :key="payment.id"
                            class="px-5 py-4 flex items-center justify-between"
                        >
                            <div>
                                <p class="text-sm font-medium text-gray-800 capitalize">
                                    {{ payment.category?.replace(/_/g, ' ') }}
                                </p>
                                <p class="text-xs text-gray-400 mt-0.5">
                                    {{ payment.reference }}
                                    · {{ payment.payment_method }}
                                </p>
                            </div>
                            <div class="text-right">
                                <p class="text-sm font-semibold text-gray-800">
                                    ₦{{ Number(payment.amount_paid).toLocaleString() }}
                                </p>
                                <span
                                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize"
                                    :class="{
                                        'bg-green-50 text-green-700': payment.status === 'paid',
                                        'bg-yellow-50 text-yellow-700': payment.status === 'pending',
                                        'bg-red-50 text-red-600': payment.status === 'failed',
                                    }"
                                >
                                    {{ payment.status }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div v-else class="px-5 py-10 text-center text-sm text-gray-400">
                        No payments yet.
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>